import React, { useEffect, useState } from 'react';
import { FaTrash, FaPlus, FaSearch, FaBook, FaChevronLeft, FaGripVertical, FaCalendarAlt, FaTimes, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { FaPencil } from "react-icons/fa6";
import Select from 'react-select';
import { motion, AnimatePresence } from 'framer-motion';
import ApiService from '../../models/ApiService';

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Festival Shayari Item Component
// Updated SortableFestivalShayariItem Component - Fix the onDelete call
const SortableFestivalShayariItem = ({ shayari, onEdit, onDelete, onToggleStatus }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: shayari._id || shayari.festival_poetry_no?.toString() || shayari.no.toString(),
    data: {
      type: 'festival_shayari',
      shayari
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-md overflow-hidden border border-[--border-color-g] hover:shadow-lg transition-all duration-300 
                ${shayari.is_active === false ? 'opacity-60' : ''} 
                ${isDragging ? 'shadow-xl ring-2 ring-[--primary-btn-color] scale-105' : ''}
                cursor-move`}
    >
      <div className="p-4">
        {/* Drag Handle and Index */}
        <div className="flex justify-between items-center mb-3 pb-2">
          <div className="flex items-center gap-3">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-2 hover:bg-[--bg-color] rounded-lg transition-colors"
              title="Drag to reorder"
            >
              <FaGripVertical className="text-[--secondary-color] text-xl" />
            </div>

          </div>
          {/* <span className="text-sm text-gray-500">No: {shayari.festival_poetry_no || shayari.no}</span> */}
        </div>

        {/* Urdu Content */}
        <div className="flex justify-end mb-4">
          <p
            className="text-lg text-[--text-color-g]"
            id='primary-urdu-fonts'
            dir='rtl'
            dangerouslySetInnerHTML={{ __html: shayari.urdu?.replace(/\n/g, '<br />') || '' }}
          />
        </div>

        {/* Poet Name in Urdu */}
        {shayari.poet_urdu && (
          <div className="flex justify-end mb-4">
            <p
              className="text-sm font-semibold text-[--text-color-g]"
              id='primary-urdu-fonts'
              dir='rtl'
              style={{ fontStyle: 'italic' }}
            >
              — {shayari.poet_urdu}
            </p>
          </div>
        )}

        {/* Devnagari Content */}
        {shayari.devnagari && (
          <div className="mb-4 bg-gray-50 p-3 rounded">
            <p className="text-base text-black"
              dangerouslySetInnerHTML={{ __html: shayari.devnagari.replace(/\n/g, '<br />') }}
            />
          </div>
        )}

        {/* Poet Name Hindi */}
        {shayari.poet_hindi && (
          <div className="flex justify-start mb-4">
            <p
              className="text-sm font-semibold text-[--text-color-g]"
              style={{ fontStyle: 'italic' }}
            >
              {shayari.poet_hindi}
            </p>
          </div>
        )}

        {/* Roman Content */}
        {shayari.roman && (
          <div className="mb-4 bg-gray-50 p-3 rounded">
            <p className="text-base text-black"
              dangerouslySetInnerHTML={{ __html: shayari.roman.replace(/\n/g, '<br />') }}
            />
          </div>
        )}

        {/* Footer with Poet Name and Actions */}
        <div className="flex justify-between items-center mt-4 pt-3 border-t border-[--border-color-g]">
          <span className="text-sm font-semibold text-[--text-color-g]">{shayari.poet}</span>

          <div className="flex items-center gap-2">
            {/* Toggle Switch */}
            <label className="flex items-center gap-2 cursor-pointer p-1 hover:bg-gray-50 rounded-lg transition-colors">
              <input
                type="checkbox"
                checked={shayari.is_published}
                onChange={() => onToggleStatus(shayari._id, shayari.is_published)}
                className="hidden"
              />
              <div className={`w-8 h-4 flex items-center rounded-full p-0.5 transition-colors 
                    ${shayari.is_published ? 'bg-green-500' : 'bg-gray-400'}`}>
                <div className={`bg-white w-3 h-3 rounded-full shadow-md transform transition-transform duration-200 
                        ${shayari.is_published ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
            </label>

            <button
              onClick={() => onEdit(shayari)}
              className="p-2 text-[--primary-btn-color] hover:bg-[--primary-btn-color] hover:text-white rounded-full transition-colors"
              title="Edit"
            >
              <FaPencil />
            </button>
            <button
              onClick={() => {
                // Pass the correct ID based on your API structure
                const deleteId = shayari.festival_poetry_no || shayari._id || shayari.no;
                console.log('Deleting shayari with ID:', deleteId, 'Full shayari data:', shayari);
                onDelete(deleteId);
              }}
              className="p-2 text-red-500 hover:bg-red-500 hover:text-white rounded-full transition-colors"
              title="Delete"
            >
              <FaTrash />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Order Management Modal Component
// Order Management Modal Component with Mobile Responsive Design
const OrderManagementModal = ({ isOpen, onClose, shayaris, festivalName, onReorder }) => {
  const [orderedShayaris, setOrderedShayaris] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [swapWithIndex, setSwapWithIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize orderedShayaris when modal opens or shayaris change
  useEffect(() => {
    if (shayaris && shayaris.length > 0) {
      // Sort by no
      const sorted = [...shayaris].sort((a, b) => {
        const orderA = a.festival_poetry_no || a.no || 0;
        const orderB = b.festival_poetry_no || b.no || 0;
        return orderA - orderB;
      });
      setOrderedShayaris(sorted);
    } else {
      setOrderedShayaris([]);
    }
  }, [shayaris, isOpen]);

  const moveUp = (index) => {
    if (index > 0) {
      const newOrder = [...orderedShayaris];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setOrderedShayaris(newOrder);
      setSelectedIndex(index - 1);
      setSwapWithIndex(null);
    }
  };

  const moveDown = (index) => {
    if (index < orderedShayaris.length - 1) {
      const newOrder = [...orderedShayaris];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setOrderedShayaris(newOrder);
      setSelectedIndex(index + 1);
      setSwapWithIndex(null);
    }
  };

  const moveToTop = (index) => {
    if (index > 0) {
      const newOrder = [...orderedShayaris];
      const [item] = newOrder.splice(index, 1);
      newOrder.unshift(item);
      setOrderedShayaris(newOrder);
      setSelectedIndex(0);
      setSwapWithIndex(null);
    }
  };

  const moveToBottom = (index) => {
    if (index < orderedShayaris.length - 1) {
      const newOrder = [...orderedShayaris];
      const [item] = newOrder.splice(index, 1);
      newOrder.push(item);
      setOrderedShayaris(newOrder);
      setSelectedIndex(orderedShayaris.length - 1);
      setSwapWithIndex(null);
    }
  };

  const handleSwap = (index1, index2) => {
    if (index1 === index2) return;
    const newOrder = [...orderedShayaris];
    [newOrder[index1], newOrder[index2]] = [newOrder[index2], newOrder[index1]];
    setOrderedShayaris(newOrder);
    setSelectedIndex(index2);
    setSwapWithIndex(null);
  };

  const handleItemClick = (index) => {
    if (swapWithIndex === null) {
      setSwapWithIndex(index);
      setSelectedIndex(index);
    } else {
      if (swapWithIndex !== index) {
        handleSwap(swapWithIndex, index);
      }
      setSwapWithIndex(null);
    }
  };

  const clearSwapSelection = () => {
    setSwapWithIndex(null);
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const startNo = Math.min(...orderedShayaris.map(item => item.festival_poetry_no || item.no || 0));

      const payload = orderedShayaris.map((item, idx) => ({
        _id: item._id,
        no: startNo + idx,
        festival_poetry_no: startNo + idx
      }));

      await onReorder(payload);
      onClose();
    } catch (error) {
      console.error('Error saving order:', error);
      alert(error.message || 'Failed to save order. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original order
    const originalSorted = [...shayaris].sort((a, b) => {
      const orderA = a.festival_poetry_no || a.no || 0;
      const orderB = b.festival_poetry_no || b.no || 0;
      return orderA - orderB;
    });
    setOrderedShayaris(originalSorted);
    setSelectedIndex(null);
    setSwapWithIndex(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col"
        >
          {/* Header - Responsive */}
          <div className="p-3 sm:p-6 border-b border-gray-200 flex justify-between items-center">
            <div className="flex-1 min-w-0 mr-2">
              <h2 className="text-lg sm:text-2xl font-bold text-[--text-color] truncate">
                Manage Order - {festivalName}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Total Shayaris: {orderedShayaris.length}
              </p>
              {swapWithIndex !== null && (
                <p className="text-xs sm:text-sm text-blue-600 mt-1 break-words">
                  <span className="hidden sm:inline">Selected item #{orderedShayaris[swapWithIndex]?.festival_poetry_no || orderedShayaris[swapWithIndex]?.no}. Click on another item to swap positions.</span>
                  <span className="sm:hidden">Tap another item to swap</span>
                  <button
                    onClick={clearSwapSelection}
                    className="ml-2 text-red-500 hover:text-red-700 underline text-xs sm:text-sm"
                  >
                    Cancel
                  </button>
                </p>
              )}
            </div>
            <button
              onClick={handleCancel}
              className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <FaTimes className="text-gray-500 text-lg sm:text-xl" />
            </button>
          </div>

          {/* Content - Responsive */}
          <div className="flex-1 overflow-y-auto p-2 sm:p-6">
            <div className="space-y-2 sm:space-y-3">
              {orderedShayaris.map((shayari, index) => (
                <motion.div
                  key={shayari._id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className={`bg-white border rounded-lg transition-all duration-200 cursor-pointer ${selectedIndex === index
                    ? 'border-[--border-color-g] shadow-lg'
                    : 'border-gray-200 hover:shadow-md'
                    } ${swapWithIndex === index
                      ? 'ring-1 ring-[--border-color-g] bg-blue-50'
                      : ''
                    }`}
                  onClick={() => handleItemClick(index)}
                >
                  {/* Mobile Layout (default) */}
                  <div className="sm:hidden p-3">
                    {/* Order Badge */}
                    <div className="flex justify-between items-center mb-2">
                      <span className={`inline-block rounded-lg px-2 py-0.5 text-xs font-semibold ${swapWithIndex === index
                        ? 'bg-[--primary-color] text-white'
                        : 'bg-gray-100 text-gray-700'
                        }`}>
                        No: {shayari.festival_poetry_no || shayari.no}
                      </span>
                      <span className="text-xs text-gray-400">
                        New: {index + 1}
                      </span>
                    </div>

                    {/* Content Preview */}
                    <div className="mb-3">
                      <p
                        className="text-sm text-[--text-color-g] break-words"
                        id='primary-urdu-fonts'
                        dir='rtl'
                        dangerouslySetInnerHTML={{
                          __html: (shayari.urdu?.substring(0, 80) || '') + (shayari.urdu?.length > 80 ? '...' : '')
                        }}
                      />
                      {shayari.devnagari && (
                        <p className="mt-1 text-xs text-gray-600 break-words">
                          {shayari.devnagari.substring(0, 60)}
                          {shayari.devnagari.length > 60 && '...'}
                        </p>
                      )}
                    </div>

                    {/* Action Buttons - Mobile */}
                    <div className="grid grid-cols-3 gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveToTop(index);
                        }}
                        disabled={index === 0}
                        className={`col-span-1 px-1 py-1.5 text-xs rounded flex items-center justify-center gap-1 ${index === 0
                          ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                          : 'text-[--text-color-g] hover:bg-gray-100 active:bg-gray-200'
                          }`}
                      >
                        <FaArrowUp className="text-xs" /> Top
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveUp(index);
                        }}
                        disabled={index === 0}
                        className={`col-span-1 px-1 py-1.5 text-xs rounded flex items-center justify-center gap-1 ${index === 0
                          ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                          : 'text-[--text-color-g] hover:bg-gray-100 active:bg-gray-200'
                          }`}
                      >
                        <FaArrowUp className="text-xs" /> Up
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (swapWithIndex !== null && swapWithIndex !== index) {
                            handleSwap(swapWithIndex, index);
                            setSwapWithIndex(null);
                          } else if (swapWithIndex === null) {
                            setSwapWithIndex(index);
                            setSelectedIndex(index);
                          } else if (swapWithIndex === index) {
                            setSwapWithIndex(null);
                          }
                        }}
                        className={`col-span-1 px-1 py-1.5 text-xs rounded flex items-center justify-center ${swapWithIndex === index
                          ? 'bg-[--primary-color] text-white'
                          : 'text-[--text-color-g] hover:bg-gray-100 active:bg-gray-200'
                          }`}
                      >
                        ⇄ Swap
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveDown(index);
                        }}
                        disabled={index === orderedShayaris.length - 1}
                        className={`col-span-1 px-1 py-1.5 text-xs rounded flex items-center justify-center gap-1 ${index === orderedShayaris.length - 1
                          ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                          : 'text-[--text-color-g] hover:bg-gray-100 active:bg-gray-200'
                          }`}
                      >
                        <FaArrowDown className="text-xs" /> Down
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          moveToBottom(index);
                        }}
                        disabled={index === orderedShayaris.length - 1}
                        className={`col-span-2 px-1 py-1.5 text-xs rounded flex items-center justify-center gap-1 ${index === orderedShayaris.length - 1
                          ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                          : 'text-[--text-color-g] hover:bg-gray-100 active:bg-gray-200'
                          }`}
                      >
                        <FaArrowDown className="text-xs" /> Bottom
                      </button>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:block p-4">
                    <div className="flex items-start gap-4">
                      {/* Order Number */}
                      <div className="flex-shrink-0 w-24 text-center">
                        <div>
                          <span className={`inline-block rounded-lg px-3 py-1 text-sm font-semibold ${swapWithIndex === index
                            ? 'bg-[--primary-color] text-white'
                            : 'bg-gray-100 text-gray-700'
                            }`}>
                            No: {shayari.festival_poetry_no || shayari.no}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          New Order: {index + 1}
                        </p>
                      </div>

                      {/* Content Preview */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-end mb-2">
                          <p
                            className="text-base text-[--text-color-g] break-words w-full"
                            id='primary-urdu-fonts'
                            dir='rtl'
                            dangerouslySetInnerHTML={{
                              __html: (shayari.urdu?.substring(0, 100) || '') + (shayari.urdu?.length > 100 ? '...' : '')
                            }}
                          />
                        </div>
                        {shayari.devnagari && (
                          <div className="mt-2 text-sm text-gray-600 break-words">
                            {shayari.devnagari.substring(0, 80)}
                            {shayari.devnagari.length > 80 && '...'}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex-shrink-0">
                        <div className="flex gap-1 mb-2 pb-2 border-b border-gray-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveToTop(index);
                            }}
                            disabled={index === 0}
                            className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${index === 0
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-[--text-color-g] hover:bg-gray-100'
                              }`}
                            title="Move to First"
                          >
                            <FaArrowUp className="text-xs" /> First
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveToBottom(index);
                            }}
                            disabled={index === orderedShayaris.length - 1}
                            className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${index === orderedShayaris.length - 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-[--text-color-g] hover:bg-gray-100'
                              }`}
                            title="Move to Last"
                          >
                            <FaArrowDown className="text-xs" /> Last
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (swapWithIndex !== null && swapWithIndex !== index) {
                                handleSwap(swapWithIndex, index);
                                setSwapWithIndex(null);
                              } else if (swapWithIndex === null) {
                                setSwapWithIndex(index);
                                setSelectedIndex(index);
                              } else if (swapWithIndex === index) {
                                setSwapWithIndex(null);
                              }
                            }}
                            className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${swapWithIndex === index
                              ? 'bg-[--primary-color] text-white'
                              : 'text-[--text-color-g] hover:bg-gray-100'
                              }`}
                            title="Swap with another shayari"
                          >
                            ⇄ Swap
                          </button>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveUp(index);
                            }}
                            disabled={index === 0}
                            className={`flex-1 px-3 py-1.5 rounded text-sm transition-colors flex items-center justify-center gap-1 ${index === 0
                              ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                              : 'text-[--text-color-g] hover:bg-gray-100'
                              }`}
                            title="Move Up"
                          >
                            <FaArrowUp size={12} /> Up
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveDown(index);
                            }}
                            disabled={index === orderedShayaris.length - 1}
                            className={`flex-1 px-3 py-1.5 rounded text-sm transition-colors flex items-center justify-center gap-1 ${index === orderedShayaris.length - 1
                              ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                              : 'text-[--text-color-g] hover:bg-gray-100'
                              }`}
                            title="Move Down"
                          >
                            <FaArrowDown size={12} /> Down
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Footer - Responsive */}
          <div className="p-3 sm:p-6 border-t border-gray-200 flex justify-end gap-2 sm:gap-3">
            <button
              onClick={handleCancel}
              className="px-3 sm:px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 sm:px-4 py-2 text-sm bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Order'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

function FestivalShayari() {
  const [festivalShayaris, setFestivalShayaris] = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [poets, setPoets] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    festival_id: '',
    festival_name: '',
    festival_poetry_no: '',
    poet_id: '',
    poet: '',
    poet_hindi: '',
    poet_urdu: '',
    urdu: '',
    roman: '',
    devnagari: '',
    no: 0,
    is_published: true
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFestival, setSelectedFestival] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReordering, setIsReordering] = useState(false);
  const [pendingOrder, setPendingOrder] = useState([]);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isOrderSaving, setIsOrderSaving] = useState(false); // Track order save state
  const [orderButtonDisabled, setOrderButtonDisabled] = useState(false); // Disable manage order button

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchFestivalShayari(),
        fetchFestivals(),
        fetchPoets()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFestivals = async () => {
    try {
      const res = await ApiService.GetFestival();
      console.log(res);

      const formattedFestivals = res.data.responseContent.map(festival => ({
        value: festival.id,
        label: festival.name,
        name: festival.name
      }));
      setFestivals(formattedFestivals);
    } catch (error) {
      console.error('Error fetching festivals:', error);
      setFestivals([]);
      throw error;
    }
  };

  const fetchPoets = async () => {
    try {
      const res = await ApiService.Shayar();
      const formattedPoets = res.data.responseContent.map(poet => ({
        value: poet.id,
        label: poet.name,
        name: poet.name,
        poet_hindi: poet.hindi || '',
        poet_urdu: poet.urdu || ''
      }));
      setPoets(formattedPoets);
    } catch (error) {
      console.error('Error fetching poets:', error);
      setPoets([]);
      throw error;
    }
  };

  const fetchFestivalShayari = async () => {
    try {
      // Don't set isFullPageLoading true here if called from handleDelete
      const res = await ApiService.GetAllFestivalShayari();
      console.log(res);

      const shayaris = res.data.responseContent || res.data;
      setFestivalShayaris(shayaris);

      // If selected festival exists, verify it still has data
      if (selectedFestival) {
        const festivalExists = shayaris.some(s => s.festival_name === selectedFestival);
        if (!festivalExists) {
          setSelectedFestival(null);
          setHasOrderChanges(false);
          setPendingOrder([]);
        }
      }
    } catch (error) {
      console.error('Error fetching festival shayari:', error);
      setFestivalShayaris([]);
      throw error;
    }
  };


  const getFilteredShayaris = () => {
    if (selectedFestival) {
      return festivalShayaris
        .filter(shayari => shayari.festival_name === selectedFestival)
        .sort((a, b) => (a.festival_poetry_no || a.no || 0) - (b.festival_poetry_no || b.no || 0));
    } else {
      return festivalShayaris.filter(shayari =>
        (shayari.festival_name && shayari.festival_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (shayari.urdu && shayari.urdu.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (shayari.poet && shayari.poet.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
  };

  const filteredShayaris = getFilteredShayaris();

  const handleFestivalChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      festival_id: selectedOption?.value || '',
      festival_name: selectedOption?.name || '',
    }));
  };

  const handlePoetChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      poet_id: selectedOption?.value || '',
      poet: selectedOption?.name || '',
      poet_hindi: selectedOption?.poet_hindi || '',
      poet_urdu: selectedOption?.poet_urdu || '',
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!formData.festival_id) {
        alert('Please select a festival');
        setIsSubmitting(false);
        return;
      }

      if (!formData.poet_id) {
        alert('Please select a poet');
        setIsSubmitting(false);
        return;
      }

      if (!formData.urdu) {
        alert('Urdu text is required');
        setIsSubmitting(false);
        return;
      }

      // Prepare the base payload (without 'no' field)
      const payload = {
        festival_id: Number(formData.festival_id),
        poet_id: Number(formData.poet_id),
        urdu: formData.urdu,
        roman: formData.roman || undefined,
        devnagari: formData.devnagari || undefined,
        poet_urdu: formData.poet_urdu || undefined,
        poet_hindi: formData.poet_hindi || undefined,
        poet: formData.poet || undefined,
        is_published: formData.is_published !== false
      };

      if (formData.id) {
        // For updates, use the festival_poetry_no as the URL parameter
        const poetryNo = formData.festival_poetry_no || formData.no;
        if (!poetryNo) {
          alert('Poetry number is missing');
          setIsSubmitting(false);
          return;
        }

        // Don't include 'no' in the body for updates
        await ApiService.UpdateFestivalShayari(poetryNo, payload);
      } else {
        // For new entries, calculate next number and include it
        const festivalItems = festivalShayaris.filter(s => s.festival_name === formData.festival_name);
        const nextNumber = festivalItems.length + 1;
        payload.no = Number(nextNumber);
        payload.festival_poetry_no = Number(nextNumber);
        await ApiService.AddFestivalShayari(payload);
      }

      await fetchFestivalShayari();
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.message || 'An error occurred while saving');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      festival_id: '',
      festival_name: '',
      poet_id: '',
      poet: '',
      poet_hindi: '',
      poet_urdu: '',
      urdu: '',
      roman: '',
      devnagari: '',
      no: null,
      festival_poetry_no: null,
      is_published: true
    });
  };

  const handleEdit = (shayari) => {
    const selectedFestivalObj = festivals.find(f => f.name === shayari.festival_name);
    const selectedPoetObj = poets.find(p => p.name === shayari.poet);

    setFormData({
      id: shayari._id,
      festival_id: selectedFestivalObj?.value || '',
      festival_name: shayari.festival_name || '',
      poet_id: selectedPoetObj?.value || '',
      poet: shayari.poet || '',
      poet_hindi: shayari.poet_hindi || '',
      poet_urdu: shayari.poet_urdu || '',
      urdu: shayari.urdu || '',
      roman: shayari.roman || '',
      devnagari: shayari.devnagari || '',
      no: shayari.festival_poetry_no || shayari.no,
      festival_poetry_no: shayari.festival_poetry_no || shayari.no,
      is_published: shayari.is_published !== false
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this festival shayari?");
    if (!confirmDelete) return;

    try {
      setIsLoading(true);

      // Delete the shayari
      await ApiService.DeleteFestivalShayari(id);

      // Fetch fresh data
      await fetchFestivalShayari();

      // Optional: Show success message
      // alert('Festival shayari deleted successfully!');

    } catch (error) {
      console.error('Error deleting festival shayari:', error);
      alert('Failed to delete festival shayari');
    } finally {
      setIsLoading(false);
    }
  };

  const [isToggling, setIsToggling] = useState(false);

  const handleToggleStatus = async (shayariId, currentStatus) => {
    if (isToggling) return;

    try {
      setIsToggling(true);

      const shayari = festivalShayaris.find(
        s => s._id === shayariId || s.festival_poetry_no === shayariId || s.no === shayariId
      );

      if (!shayari) {
        console.error('Shayari not found');
        setIsToggling(false);
        return;
      }

      const poetryNo = shayari.festival_poetry_no || shayari.no;

      if (!poetryNo || isNaN(poetryNo)) {
        console.error('Invalid poetry number:', poetryNo);
        setIsToggling(false);
        return;
      }

      const newStatus = !currentStatus;

      await ApiService.ToggleFestivalShayari(Number(poetryNo));

      setFestivalShayaris(prev =>
        prev.map(shayari =>
          (shayari._id === shayariId || shayari.festival_poetry_no === shayariId || shayari.no === shayariId)
            ? { ...shayari, is_published: newStatus }
            : shayari
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setIsToggling(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const activeItem = filteredShayaris.find(
      item => (item._id || item.festival_poetry_no?.toString()) === active.id
    );

    const overItem = filteredShayaris.find(
      item => (item._id || item.festival_poetry_no?.toString()) === over.id
    );

    if (!activeItem || !overItem || activeItem.festival_name !== overItem.festival_name) {
      return;
    }

    const currentShayaris = festivalShayaris
      .filter(shayari => shayari.festival_name === selectedFestival)
      .sort((a, b) => (a.festival_poetry_no || a.no || 0) - (b.festival_poetry_no || b.no || 0));

    const oldIndex = currentShayaris.findIndex(
      item => (item._id || item.festival_poetry_no?.toString()) === active.id
    );

    const newIndex = currentShayaris.findIndex(
      item => (item._id || item.festival_poetry_no?.toString()) === over.id
    );

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(currentShayaris, oldIndex, newIndex);

    const startNo = Math.min(...currentShayaris.map(item => item.festival_poetry_no || item.no || 0));

    const updated = reordered.map((item, idx) => ({
      ...item,
      no: startNo + idx,
      festival_poetry_no: startNo + idx
    }));

    // Update UI immediately
    const updatedAll = festivalShayaris.map(shayari => {
      if (shayari.festival_name === selectedFestival) {
        const found = updated.find(u => u._id === shayari._id);
        return found || shayari;
      }
      return shayari;
    });

    setFestivalShayaris(updatedAll);

    // Store pending order
    const payload = updated.map(item => ({
      _id: item._id,
      no: item.festival_poetry_no || item.no,
      festival_poetry_no: item.festival_poetry_no || item.no
    }));

    setPendingOrder(payload);
    setHasOrderChanges(true);
  };

  const handleConfirmOrder = async () => {
    if (!pendingOrder.length || isOrderSaving) return;

    try {
      setIsOrderSaving(true);
      setOrderButtonDisabled(true);
      await ApiService.UpdateFestivalShayariNo(pendingOrder);
      await fetchFestivalShayari();
      setHasOrderChanges(false);
      setPendingOrder([]);
      console.log("Order updated successfully");
    } catch (error) {
      console.error("Order update failed:", error);
      alert("Failed to update order");
      await fetchFestivalShayari();
    } finally {
      setIsOrderSaving(false);
      // Re-enable after a short delay to prevent double clicks
      setTimeout(() => {
        setOrderButtonDisabled(false);
      }, 1000);
    }
  };

  const handleReorderFromModal = async (orderPayload) => {
    try {
      setIsReordering(true);
      console.log("Received order payload:", orderPayload);

      const response = await ApiService.UpdateFestivalShayariNo(orderPayload);
      console.log("API response:", response);

      await fetchFestivalShayari();
      setHasOrderChanges(false);
      setPendingOrder([]);
      console.log("Order updated successfully from modal");
    } catch (error) {
      console.error("Order update failed:", error);
      console.error("Error details:", error.response?.data);
      throw new Error(error.response?.data?.message || "Failed to update order");
    } finally {
      setIsReordering(false);
    }
  };

  const handleOpenOrderModal = () => {
    if (orderButtonDisabled) return;
    setOrderButtonDisabled(true);
    setIsOrderModalOpen(true);
    // Re-enable after a short delay
    setTimeout(() => {
      setOrderButtonDisabled(false);
    }, 1000);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[--primary-color]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen gap-4">
        <p className="text-red-500 text-lg">{error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color]"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto p-4" id='primary-fonts'>
      
      <h1 className="text-3xl font-bold mb-8 text-[--text-color] flex items-center gap-2">
        <FaCalendarAlt className="text-[--primary-color]" /> Festival Shayari Collection
      </h1>

      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div className="relative w-full md:w-96">
          {selectedFestival ? (
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedFestival(null)}
                className="flex items-center gap-2 text-[--text-color-g] px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <FaChevronLeft /> Back to all festivals
              </button>
              <button
                onClick={handleOpenOrderModal}
                disabled={orderButtonDisabled || filteredShayaris.length === 0}
                className={`flex items-center gap-2 text-white bg-[--primary-btn-color] px-4 py-2 rounded-lg hover:bg-[--secondary-btn-color] transition-colors ${orderButtonDisabled || filteredShayaris.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[--primary-btn-color]  hover:bg-[--secondary-btn-color]'
                  }`}
              >
                <FaGripVertical /> Manage Order
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                placeholder="Search festival shayari by content, festival or poet..."
                className="w-full pl-10 pr-4 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
            </>
          )}
        </div>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="w-full md:w-auto bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-6 py-2 rounded-lg flex items-center gap-2 transition duration-200 shadow-md"
        >
          <FaPlus /> Add Festival Shayari
        </button>
      </div>

      {!selectedFestival ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...new Set(
            festivalShayaris
              .filter(shayari =>
                shayari.festival_name && shayari.festival_name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map(shayari => shayari.festival_name)
          )].filter(Boolean).map((festival) => {
            const festivalItems = festivalShayaris.filter(s => s.festival_name === festival);
            return (
              <div
                key={festival}
                onClick={() => setSelectedFestival(festival)}
                className="bg-white p-6 rounded-lg shadow-md border border-[--border-color-g] hover:shadow-lg transition duration-300 cursor-pointer text-center"
              >
                <div className="text-xl font-semibold text-[--text-color-g]">{festival}</div>
                <div className="text-sm text-gray-500 mt-1">
                  {festivalItems.length} shayari
                </div>
              </div>
            );
          })}
          {festivalShayaris.length === 0 && (
            <div className="col-span-full text-center py-10">
              <p className="text-lg text-gray-600">No festival shayaris found</p>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[--text-color] flex items-center gap-2">
              <span className="text-[--text-color-g]">{selectedFestival}</span>
            </h2>
            {isReordering && (
              <span className="text-sm text-blue-500 animate-pulse flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                Updating order...
              </span>
            )}
            {hasOrderChanges && !isReordering && (
              <button
                onClick={handleConfirmOrder}
                disabled={isOrderSaving}
                className={`px-4 py-2 rounded-lg ${isOrderSaving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
                  } text-white`}
              >
                {isOrderSaving ? 'Saving...' : 'Confirm Order'}
              </button>
            )}
          </div>

          {filteredShayaris.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredShayaris.map(s => s._id || s.festival_poetry_no?.toString() || s.no.toString())}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredShayaris.map((shayari) => (
                    <SortableFestivalShayariItem
                      key={shayari._id || shayari.festival_poetry_no || shayari.no}
                      shayari={shayari}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleStatus={handleToggleStatus}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="text-center py-10">
              <p className="text-lg text-gray-600">No shayaris found for this festival</p>
              <button
                onClick={() => {
                  resetForm();
                  const selectedFestivalObj = festivals.find(f => f.name === selectedFestival);
                  if (selectedFestivalObj) {
                    setFormData(prev => ({
                      ...prev,
                      festival_name: selectedFestival,
                      festival_id: selectedFestivalObj.value
                    }));
                  }
                  setIsModalOpen(true);
                }}
                className="mt-4 bg-[--primary-btn-color] text-white px-4 py-2 rounded-lg hover:bg-[--secondary-btn-color]"
              >
                Add First Shayari
              </button>
            </div>
          )}
        </div>
      )}

      {/* Order Management Modal */}
      <OrderManagementModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        shayaris={filteredShayaris}
        festivalName={selectedFestival}
        onReorder={handleReorderFromModal}
      />

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-screen-md max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <h2 className="text-xl font-semibold text-[--text-color] mb-4">
                  {formData.id ? 'Edit Festival Shayari' : 'Add New Festival Shayari'}
                </h2>
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Festival
                    </label>
                    <Select
                      options={festivals}
                      value={festivals.find(option => option.value === formData.festival_id) || null}
                      onChange={handleFestivalChange}
                      placeholder="Select a festival"
                      className="w-full"
                      classNamePrefix="react-select"
                      isClearable
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Poet
                    </label>
                    <Select
                      options={poets}
                      value={poets.find(option => option.value === formData.poet_id) || null}
                      onChange={handlePoetChange}
                      placeholder="Select a poet"
                      className="w-full"
                      classNamePrefix="react-select"
                      isClearable
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Urdu Shayari
                    </label>
                    <textarea
                      id="primary-urdu-fonts"
                      name="urdu"
                      value={formData.urdu}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg"
                      rows="4"
                      dir='rtl'
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Devnagari
                    </label>
                    <textarea
                      name="devnagari"
                      value={formData.devnagari}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg"
                      rows="3"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Roman
                    </label>
                    <textarea
                      name="roman"
                      value={formData.roman}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg"
                      rows="3"
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Saving..." : formData.id ? "Update" : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

export default FestivalShayari;